/**
 * The Amazon Ads grant relayed from the OAuth popup back to the page that opened it.
 *
 * The grant decides which Shops exist and which seller the Selling Partner
 * authorization then names, so it is validated like any other external input —
 * a same-origin frame must not be able to forge one.
 */
import { amazonProfileSchema } from "@adpilot/shared";
import { z } from "zod";

export const AMAZON_ADS_MESSAGE_TYPE = "amazon-connected" as const;

export const amazonAdsGrantSchema = z.object({
  profiles: z.array(amazonProfileSchema),
  selectionState: z.string().trim().min(1).max(512),
  /** Amazon login that completed the grant; absent when Amazon returns none. */
  accountEmail: z.string().max(320).nullable().optional(),
});

export type AmazonAdsGrant = z.infer<typeof amazonAdsGrantSchema>;

const amazonAdsGrantMessageSchema = z.object({
  type: z.literal(AMAZON_ADS_MESSAGE_TYPE),
  success: z.literal(true),
  data: amazonAdsGrantSchema,
});

/** Relay the validated grant to the opener, pinned to the current AdPraxis origin. */
export function notifyAmazonAdsOpener(
  opener: Pick<Window, "postMessage">,
  origin: string,
  grant: AmazonAdsGrant,
): void {
  opener.postMessage({ type: AMAZON_ADS_MESSAGE_TYPE, success: true, data: grant }, origin);
}

/** Accept a grant only from the exact popup opened by this page on this origin. */
export function parseTrustedAmazonAdsGrant(
  event: Pick<MessageEvent, "data" | "origin" | "source">,
  expectedOrigin: string,
  expectedSource: Window | null,
): AmazonAdsGrant | null {
  if (event.origin !== expectedOrigin) return null;
  if (!expectedSource || event.source !== expectedSource) return null;
  const parsed = amazonAdsGrantMessageSchema.safeParse(event.data);
  return parsed.success ? parsed.data.data : null;
}
